// The Audit log (no board: the admin boards' master brand look). Owner and admin only (the shell and
// the read: hub.audit_log_page, docs/data-model.md 10.4.2). Every grant, revoke, pause, merge,
// delete, role change and view as member, newest first, filtered by the person it was about, who did
// it, the action, the product and the dates. The log holds ids; names are read now, and an anonymised
// person reads "Deleted person", with no link to a page.
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from '../../lib/router.jsx'
import {
  PageHeader, Card, Field, Select, DateField, SearchBox, Table, Button, Loading, Problem,
  useAdmin, useLoad, ukDateTime, ukToIso, ukEndOfDay, plural, adminPath,
} from '../ui/index.js'
import { ACTION_GROUPS, ACTION_GROUP_NAMES, actionWord, actorWord, detailWord, PAGE } from '../prices/audit.js'
import '../../styles/admin-prices.css'

const deleted = name => !name || name === 'Deleted person'

function PersonFilter({ value, onChange }) {
  const { api } = useAdmin()
  const [q, setQ] = useState('')
  const [found, setFound] = useState(null)
  const [error, setError] = useState(null)
  const asked = useRef(0)

  async function search(text) {
    setQ(text)
    if (text.trim().length < 2) { setFound(null); return }
    const n = ++asked.current
    try {
      const rows = await api.staff_people({ search: text.trim(), limit: 6 })
      if (n === asked.current) { setFound(rows); setError(null) }
    } catch (e) {
      if (n === asked.current) setError(api.words(e))
    }
  }

  if (value) {
    return (
      <div className="au-person">
        <span className="ad-label">About</span>
        <span className="au-picked">{value.full_name}<Button size="xs" onClick={() => onChange(null)}>Clear</Button></span>
      </div>
    )
  }
  return (
    <div className="au-person">
      <span className="ad-label" aria-hidden="true">About</span>
      <SearchBox label="Find the person it was about" value={q} onChange={search} />
      {error && <p className="ad-error" role="alert">{error}</p>}
      {found && found.length === 0 && <p className="pm-match-none">Nobody in the hub matches that.</p>}
      {found && found.length > 0 && (
        <ul className="pm-match-list" aria-label="People who match">
          {found.map(p => (
            <li key={p.person_id}>
              <span className="pm-match-who"><span className="pm-match-name">{p.full_name}</span><span className="pm-match-email">{p.email}</span></span>
              <Button size="xs" onClick={() => { onChange({ id: p.person_id, full_name: p.full_name }); setQ(''); setFound(null) }}>Pick</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default function Audit() {
  const { api, products } = useAdmin()
  const [filters, setFilters] = useState({ person: null, actor: '', action: '', product: '', from: '', to: '' })
  const set = patch => setFilters(f => ({ ...f, ...patch }))
  const [page, setPage] = useState({ status: 'loading', rows: [], more: false, error: null, busy: false })
  const latest = useRef(0)

  // who can have done something: the staff
  const staff = useLoad(() => api.staff_people({ role: 'staff', include_test: true, limit: 200 }), [])
  const productOptions = useMemo(() => (products || []).map(p => ({ value: p.id, label: p.name })), [products])
  const datesWrong = filters.from && filters.to && filters.from > filters.to

  async function fetchPage(beforeId, f) {
    const rows = await api.audit_log_page({
      before_id: beforeId ?? undefined, limit: PAGE, action: f.action || undefined, actor: f.actor || undefined,
      target: f.person?.id || undefined, product: f.product || undefined,
    })
    const fromAt = f.from ? Date.parse(ukToIso(f.from, '00:00')) : null
    const kept = fromAt ? rows.filter(r => Date.parse(r.happened_at) >= fromAt) : rows
    // a full page with nothing older than From yet may have more behind it
    const more = rows.length === PAGE && kept.length === rows.length
    return { kept, more, last: rows.length ? rows[rows.length - 1].id : null }
  }

  async function load(f) {
    const n = ++latest.current
    setPage({ status: 'loading', rows: [], more: false, error: null, busy: false })
    try {
      let before = null
      if (f.to) {
        // the newest entry on or before the To day: the log is read from just after it
        const end = ukEndOfDay(f.to)
        const top = await api.run(api.from('audit_log').select('id').lte('at', end).order('id', { ascending: false }).limit(1))
        if (!top.length) {
          if (n === latest.current) setPage({ status: 'ready', rows: [], more: false, error: null, busy: false, last: null })
          return
        }
        before = top[0].id + 1
      }
      const got = await fetchPage(before, f)
      if (n === latest.current) setPage({ status: 'ready', rows: got.kept, more: got.more, last: got.last, error: null, busy: false })
    } catch (e) {
      if (n === latest.current) setPage({ status: 'error', rows: [], more: false, error: { message: api.words(e) }, busy: false })
    }
  }

  async function more() {
    const n = latest.current
    setPage(p => ({ ...p, busy: true, error: null }))
    try {
      const got = await fetchPage(page.last, filters)
      if (n === latest.current) setPage(p => ({ ...p, rows: [...p.rows, ...got.kept], more: got.more, last: got.last, busy: false }))
    } catch (e) {
      if (n === latest.current) setPage(p => ({ ...p, busy: false, error: { message: api.words(e) } }))
    }
  }

  const key = JSON.stringify({ ...filters, person: filters.person?.id || null })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!datesWrong) load(filters) }, [key])

  const columns = [
    { key: 'when', label: 'When', width: '150px', render: r => <span className="au-when">{ukDateTime(r.happened_at)}</span> },
    {
      key: 'what', label: 'What', width: 'minmax(0, 1.5fr)', primary: true,
      render: r => {
        const detail = detailWord(r)
        return (
          <span className="au-what">
            <span className="au-what-action">{actionWord(r.action)}</span>
            {detail && <span className="au-what-detail">{detail}</span>}
          </span>
        )
      },
    },
    { key: 'who', label: 'Done by', width: 'minmax(0, 1fr)', render: r => <span>{actorWord(r)}</span> },
    {
      key: 'about', label: 'About', width: 'minmax(0, 1.3fr)',
      render: r => (
        <span className="au-about">
          {r.target_person_id && (deleted(r.target_name)
            ? <span>{r.target_name || 'Deleted person'}</span>
            : <Link to={adminPath.person(r.target_person_id)}>{r.target_name}</Link>)}
          {r.product_name && <span className="ad-td-sub">{r.product_name}</span>}
          {!r.target_person_id && !r.product_name && <span className="ad-td-quiet">The hub as a whole</span>}
        </span>
      ),
    },
    { key: 'reason', label: 'Reason', width: 'minmax(0, 1fr)', render: r => (r.reason ? <span className="ad-td-quiet">{r.reason}</span> : null) },
  ]

  const any = filters.person || filters.actor || filters.action || filters.product || filters.from || filters.to
  return (
    <>
      <PageHeader eyebrow="Every change, and who made it" title="Audit log" />
      <Card title="Filters" aside="Grants, revokes, pauses, merges, deletes, role changes and views as a member, with everything else the hub logs">
        <div className="au-filters">
          <div className="au-filter-row">
            <PersonFilter value={filters.person} onChange={person => set({ person })} />
            <Field label="Done by">
              {props => (
                <Select {...props} value={filters.actor} onChange={actor => set({ actor })} placeholder="Anyone"
                  options={(staff.data || []).map(s => ({ value: s.person_id, label: s.full_name }))} />
              )}
            </Field>
            <Field label="What">
              {props => (
                // the actions in their groups, Accountability among them (docs/mastermind-admin-plan.md 3.5)
                <select {...props} className="ad-select" value={filters.action ?? ''} onChange={e => set({ action: e.target.value })}>
                  <option value="">Every change</option>
                  {ACTION_GROUPS.map((g, i) => (
                    <optgroup key={ACTION_GROUP_NAMES[i]} label={ACTION_GROUP_NAMES[i]}>
                      {g.map(a => <option key={a} value={a}>{actionWord(a)}</option>)}
                    </optgroup>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Product">
              {props => <Select {...props} value={filters.product} onChange={product => set({ product })} placeholder="Every product" options={productOptions} />}
            </Field>
          </div>
          <div className="au-filter-row">
            <DateField label="From" value={filters.from} onChange={from => set({ from })} max={filters.to || undefined} />
            <DateField label="To" value={filters.to} onChange={to => set({ to })} min={filters.from || undefined}
              error={datesWrong ? 'Pick a date on or after the From date.' : undefined} />
            {any && <Button size="sm" onClick={() => setFilters({ person: null, actor: '', action: '', product: '', from: '', to: '' })}>Clear the filters</Button>}
          </div>
        </div>
      </Card>

      {page.status === 'loading' && <Loading />}
      {page.status === 'error' && <Problem error={page.error} onRetry={() => load(filters)} />}
      {page.status === 'ready' && (
        <>
          <p className="rp-count" role="status"><strong>{plural(page.rows.length, 'entry', 'entries')}</strong>{page.more ? ', newest first. There are more.' : ', newest first.'}</p>
          <Table label="Audit log" className="au-table" columns={columns} rows={page.rows} rowKey={r => r.id}
            empty={any ? 'Nothing in the log matches these filters.' : 'Nothing is in the log yet.'} />
          {page.error && <p className="ad-error" role="alert">{page.error.message}</p>}
          {page.more && (
            <div className="au-more"><Button busy={page.busy} busyLabel="Loading" onClick={more}>Show {PAGE} more</Button></div>
          )}
        </>
      )}
    </>
  )
}
